import {
  BPC_ACTUALS_KPI_COLUMNS,
  COST_OF_GOODS_CATEGORIES_WITH_YEAR_SUFFIX,
  GOOGLE_FINANCIAL_STATEMENT_TYPE_COMMENT_HEADERS,
  GOOGLE_FINANCIAL_STATEMENT_TYPE_HEADERS,
  MARKING_CODE_VALUES,
} from '@repo/constants'
import { getSchemaKeys } from '@repo/zod-schemas'
import { format, isValid, parse } from 'date-fns'
import { z } from 'zod'
import {
  csvBooleanSchema,
  csvErpSchema,
  csvMaterialSchema,
  csvXEmptyBooleanSchema,
  distributionChannelNameSchema,
  accountingNumberSchema,
  parsedNumberSchema,
  provinceNameSchema,
  trimLeadingZerosSchema,
} from '../schema'

export const replaceMaterialsAssortmentSchema = z.object({
  old_material: csvMaterialSchema,
  new_material: csvMaterialSchema,
  province: z.string(),
})
export const replaceMaterialsAssortmentHeaders = getSchemaKeys(replaceMaterialsAssortmentSchema)

export const replaceMaterialsLaunchesSchema = z.object({
  old_material: csvMaterialSchema,
  new_material: csvMaterialSchema,
  launch_name: z.string(),
})
export const replaceMaterialsLaunchesHeaders = getSchemaKeys(replaceMaterialsLaunchesSchema)

export const editAssortmentSchema = z.object({
  province: z.string(),
  banner: csvErpSchema,
  material: csvMaterialSchema,
  is_order: csvBooleanSchema,
  is_strategic: csvBooleanSchema,
  is_new_product: csvBooleanSchema,
  is_featured: csvBooleanSchema,
  is_sellin: csvBooleanSchema,
  is_pcapture: csvBooleanSchema,
  is_out_of_stock: csvBooleanSchema,
  sequence: z.coerce.number(),
})
export const editAssortmentHeaders = getSchemaKeys(editAssortmentSchema)

export const launchesAddMaterialsSchema = z.object({
  material: csvMaterialSchema,
  is_order: csvBooleanSchema,
  is_strategic: csvBooleanSchema,
  is_new_product: csvBooleanSchema,
  is_featured: csvBooleanSchema,
  is_sellin: csvBooleanSchema,
  is_pcapture: csvBooleanSchema,
  is_out_of_stock: csvBooleanSchema,
  sequence: z.string(),
})
export const launchesAddMaterialsHeaders = getSchemaKeys(launchesAddMaterialsSchema)

export const launchesAddErpSchema = z.object({
  sap_erp: z.string(),
})
export const launchesAddErpHeaders = getSchemaKeys(launchesAddErpSchema)

export const launchesDeleteMaterialsSchema = z.object({
  material: csvMaterialSchema,
})
export const launchesDeleteMaterialsHeaders = getSchemaKeys(launchesDeleteMaterialsSchema)

export const launchesDeleteErpSchema = z.object({
  sap_erp: z.string(),
})
export const launchesDeleteErpHeaders = getSchemaKeys(launchesDeleteErpSchema)

export const editMaterialsSequencesSchema = z.object({
  material: z.string(),
  sequence: z.string(),
})
export const editMaterialsSequencesHeaders = getSchemaKeys(editMaterialsSequencesSchema)

export const importChannelMixDetailsSchema = z.object({
  MATERIAL_GROUP_NAME_ID: z.string(),
  MATERIAL_GROUP_NAME: z.string(),
  BRAND_NAME_ID: z.string(),
  BRAND_NAME: z.string(),
  TAX_PROVINCE_NAME_ID: z.string(),
  TAX_PROVINCE_NAME: provinceNameSchema,
  MARKING_NAME_ID: z.string(),
  MARKING_NAME: z.string(),
  MATERIAL_ID: trimLeadingZerosSchema,
  DISTRIBUTION_CHANNEL_NAME_ID: z.string(),
  DISTRIBUTION_CHANNEL_NAME: distributionChannelNameSchema,
  CUSTOMER_TYPE: z.string(),
  INVOICE_ITEM_PRODUCT_CEQ_QUANTITY: z.union([parsedNumberSchema, z.number()]),
})
export const importChannelMixDetailsHeaders = getSchemaKeys(importChannelMixDetailsSchema)

export const importProductTypeVariantDetailsSchema = z.object({
  MATERIAL_ID: trimLeadingZerosSchema,
  SALES_ORG: z.string().nullable().default(null),
  DISTRIBUTION_CHANNEL: z.string().nullable().default(null),
  PROVINCE: z.string().nullable().default(null),
  BRAND_HOUSE: z.string().nullable().default(null),
  BRAND_PRODUCT: z.string().nullable().default(null),
  ITEM_PER_PACK: z.string().nullable().default(null),
  FLAVOUR: z.string().nullable().default(null),
  MATERIAL_LENGTH: z.string().nullable().default(null),
  PRODUCT_TYPE: z.string(),
})
export const importProductTypeVariantDetailsHeaders = getSchemaKeys(
  importProductTypeVariantDetailsSchema,
)

/** A CSV cell that may be blank. Blank means "leave the stored value unchanged" on import. */
const optionalCsvCellSchema = z
  .string()
  .optional()
  .default('')
  .transform((value) => value.trim())

/**
 * The unreleased materials export/import file. The key order is the template and the export column
 * order, so the file round-trips. `brand` is export-only: it derives from `brand_house_code` and is
 * ignored on import. `material_group` is the code and `material_group_name` the English name; either
 * resolves the group on import.
 */
export const importUnreleasedMaterialsSchema = z.object({
  material: optionalCsvCellSchema,
  material_group: optionalCsvCellSchema,
  material_group_name: optionalCsvCellSchema,
  brand_house_code: optionalCsvCellSchema,
  brand: optionalCsvCellSchema,
  variant: optionalCsvCellSchema,
  product_type: optionalCsvCellSchema,
  pack_content_size: optionalCsvCellSchema,
  price_segment: optionalCsvCellSchema,
  marking: optionalCsvCellSchema,
})
export const importUnreleasedMaterialsHeaders = getSchemaKeys(importUnreleasedMaterialsSchema)
export type ImportUnreleasedMaterialsRecord = z.infer<typeof importUnreleasedMaterialsSchema>

/**
 * Zod schema for CSV date strings that transforms YYYYMMDD format to YYYY-MM-DD format.
 * Handles null, empty strings, and various date formats.
 */
const csvDateSchema = z
  .string()
  .nullable()
  .default(null)
  .transform((dateString) => {
    if (!dateString || dateString.trim() === '' || dateString === '0') return null

    const dateTrimmed = dateString.trim()
    if (dateTrimmed.length === 8) {
      const parsedDate = parse(dateTrimmed, 'yyyyMMdd', new Date())
      if (!isValid(parsedDate)) return null
      return format(parsedDate, 'yyyy-MM-dd')
    }

    const parsedDate = new Date(dateTrimmed + 'T00:00:00')
    if (!isValid(parsedDate)) return null
    return format(parsedDate, 'yyyy-MM-dd')
  })

export const importAgreementDetailsSchema = z.object({
  AGREEMENT_NUMBER: trimLeadingZerosSchema,
  AGREEMENT_DESCRIPTION: z.string().nullable().default(null),
  AGREEMENT_EXTERNAL_DESCRIPTION: z.string().nullable().default(null),
  SALES_ORGANIZATION: z.string().nullable().default(null),
  DISTRIBUTION_CHANNEL: z.string().nullable().default(null),
  BUDGET_ALLOCATION: z.string().nullable().default(null),
  CONTRACT_TYPE: z.string().nullable().default(null),
  MASTER_AGREEMENT: z.string().nullable().default(null),
  KSCHL: z.string().nullable().default(null),
  VALID_TO: csvDateSchema,
  VALID_FROM: csvDateSchema,
})
export const importAgreementDetailsHeaders = getSchemaKeys(importAgreementDetailsSchema)

export const importRetailerRatesSchema = z.object({
  category: z.string(),
  province: z.string(),
  gst: z.string(),
  pst: z.string(),
  hst: z.string(),
  qst: z.string(),
})
export const importRetailerRatesHeaders = getSchemaKeys(importRetailerRatesSchema)

export const importConsumerRatesSchema = z.object({
  category: z.string(),
  province: z.string(),
  gst: z.string(),
  pst: z.string(),
  hst: z.string(),
  qst: z.string(),
})
export const importConsumerRatesHeaders = getSchemaKeys(importConsumerRatesSchema)

// Cost of Goods Details uses flexible schema with passthrough since columns are dynamic
export const importCostOfGoodsDetailsSchema = z
  .object({
    Material: trimLeadingZerosSchema,
  })
  .passthrough()

export const importCostOfGoodsDetailsHeaders = [
  'Material',
  ...COST_OF_GOODS_CATEGORIES_WITH_YEAR_SUFFIX.flatMap((category) =>
    Array.from({ length: 12 }, (_, i) => `${category} ${i + 1}`),
  ),
]

// RAP Forecasts Details
export const importRapForecastsDetailsSchema = z.object({
  sku: trimLeadingZerosSchema,
  bucket: z.string().nullable(),
  broader_bucket: z.string().nullable(),
  master_agreement: z.string().nullable(),
  agreement_number: trimLeadingZerosSchema.nullable(),
  payment_type: z.string().nullable(),
  material_group: z.string().nullable(),
  channel: z.string().nullable(),
  province: z.string().nullable(),
  house: z.string().nullable(),
  brand_variant: z.string().nullable(),
  pack_size: z.string().nullable(),
  brand_variant_pack_province_channel: z.string().nullable(),
  footprint: z.string().nullable(),
  multipack_conversion: z.string().nullable(),
  investment_ctn: z.string().nullable(),
  banner: trimLeadingZerosSchema.nullable(),
  banner_desc: z.string().nullable(),
  cy_01: z.string().nullable(),
  cy_02: z.string().nullable(),
  cy_03: z.string().nullable(),
  cy_04: z.string().nullable(),
  cy_05: z.string().nullable(),
  cy_06: z.string().nullable(),
  cy_07: z.string().nullable(),
  cy_08: z.string().nullable(),
  cy_09: z.string().nullable(),
  cy_10: z.string().nullable(),
  cy_11: z.string().nullable(),
  cy_12: z.string().nullable(),
  ny_01: z.string().nullable(),
  ny_02: z.string().nullable(),
  ny_03: z.string().nullable(),
  ny_04: z.string().nullable(),
  ny_05: z.string().nullable(),
  ny_06: z.string().nullable(),
  ny_07: z.string().nullable(),
  ny_08: z.string().nullable(),
  ny_09: z.string().nullable(),
  ny_10: z.string().nullable(),
  ny_11: z.string().nullable(),
  ny_12: z.string().nullable(),
  crt_period: z.string().nullable(),
})

export const importRapForecastsDetailsHeaders = getSchemaKeys(importRapForecastsDetailsSchema)

// Sales Deal Actuals Details
export const importSalesDealActualsDetailsSchema = z.object({
  CustCol_40: z.string().nullable(),
  CUSTOMER_NUMBER: trimLeadingZerosSchema,
  MATERIAL_ID: trimLeadingZerosSchema,
  SALES_DEAL_DESCRIPTION: z.string().nullable().default(null),
  SALES_DEAL_CODE: trimLeadingZerosSchema,
  CustCol_22: z.string().nullable().default(null),
  INVOICE_ITEM_TRADE_TERM_AMOUNT: parsedNumberSchema,
})

export const importSalesDealActualsDetailsHeaders = getSchemaKeys(
  importSalesDealActualsDetailsSchema,
)

export const importDistributionMarginMixDetailsSchema = z.object({
  condition_type: z.string().nullable().default(null),
  sales_organization: z.string().nullable().default(null),
  distribution_channel: z.string().nullable().default(null),
  customer_number: trimLeadingZerosSchema.nullable().default(null),
  product_hierarchy_level1: z.string().nullable().default(null),
  validity_start_date: csvDateSchema,
  validity_end_date: csvDateSchema,
  condition_record_number: trimLeadingZerosSchema.nullable().default(null),
  condition_amount: parsedNumberSchema.nullable().default(null),
  condition_currency: z.string().nullable().default(null),
  pricing_unit: z.string().nullable().default(null),
  unit_of_measure: z.string().nullable().default(null),
})

export const importDistributionMarginMixDetailsHeaders = getSchemaKeys(
  importDistributionMarginMixDetailsSchema,
)
// IP Accruals Details
const csvNullableStringSchema = z
  .string()
  .nullable()
  .transform((v) => (v && v.toUpperCase() === 'NULL' ? null : v))

export const importIpAccrualsDetailsSchema = z.object({
  SALES_ORG: z.string().nullable(),
  BILLING_DATE: csvDateSchema,
  CONDITION_TYPE: z.string().nullable(),
  CUSTOMER_NUMBER: trimLeadingZerosSchema.nullable(),
  AGREEMENT_NUMBER: trimLeadingZerosSchema.nullable(),
  MATERIAL_SKU: trimLeadingZerosSchema,
  IP_AMOUNT: parsedNumberSchema,
  BANNER: trimLeadingZerosSchema.nullable(),
  BUDGET_ALLOCATION: csvNullableStringSchema,
  IP_TYPE: z.string().nullable(),
})

export const importIpAccrualsDetailsHeaders = getSchemaKeys(importIpAccrualsDetailsSchema)

export const importBudgetAllocationsDetailsSchema = z.object({
  ZZ_VX_BG_ALLOC: z.string(),
  ZZ_DESC: z.string().nullable(),
  ZZ_VALIDITY: csvXEmptyBooleanSchema,
})

export const importBudgetAllocationsDetailsHeaders = getSchemaKeys(
  importBudgetAllocationsDetailsSchema,
)

// Calculation Run Accruals Details
export const importCalculationRunAccrualsDetailsSchema = z.object({
  CUSTOMER_NUMBER: trimLeadingZerosSchema.nullable(),
  AGREEMENT_NUMBER: trimLeadingZerosSchema.nullable(),
  MATERIAL_SKU: trimLeadingZerosSchema.pipe(z.string().min(1)),
  AMOUNT: parsedNumberSchema,
  BUDGET_ALLOCATION: csvNullableStringSchema,
  BILLING_DATE: csvDateSchema,
  CONDITION_TYPE: csvNullableStringSchema,
})

export const importCalculationRunAccrualsDetailsHeaders = getSchemaKeys(
  importCalculationRunAccrualsDetailsSchema,
)

// NC Provincial Contributions Details. Shared by every hand-made CRT monthly import (volumes, BPC
// actuals): cells come straight out of Excel, so a lone dash is zero and `(176.06)` is a negative.
const ncVolumeSchema = z
  .union([
    z
      .string()
      .regex(/^\s*-\s*$/)
      .transform(() => 0),
    accountingNumberSchema,
    z.number(),
  ])
  .default(0)

export const importNcProvincialContributionsDetailsSchema = z.object({
  sku: trimLeadingZerosSchema,
  province_of_tax_cg1: provinceNameSchema,
  cy_jan: ncVolumeSchema,
  cy_feb: ncVolumeSchema,
  cy_mar: ncVolumeSchema,
  cy_apr: ncVolumeSchema,
  cy_may: ncVolumeSchema,
  cy_jun: ncVolumeSchema,
  cy_jul: ncVolumeSchema,
  cy_aug: ncVolumeSchema,
  cy_sep: ncVolumeSchema,
  cy_oct: ncVolumeSchema,
  cy_nov: ncVolumeSchema,
  cy_dec: ncVolumeSchema,
  ny_jan: ncVolumeSchema,
  ny_feb: ncVolumeSchema,
  ny_mar: ncVolumeSchema,
  ny_apr: ncVolumeSchema,
  ny_may: ncVolumeSchema,
  ny_jun: ncVolumeSchema,
  ny_jul: ncVolumeSchema,
  ny_aug: ncVolumeSchema,
  ny_sep: ncVolumeSchema,
  ny_oct: ncVolumeSchema,
  ny_nov: ncVolumeSchema,
  ny_dec: ncVolumeSchema,
})

export const importNcProvincialContributionsDetailsHeaders = getSchemaKeys(
  importNcProvincialContributionsDetailsSchema,
)

// Channel Contributions Details
export const importChannelContributionsDetailsSchema = z.object({
  sku: trimLeadingZerosSchema,
  pricing_sub_channel: z.string().nullable(),
  cy_jan: ncVolumeSchema,
  cy_feb: ncVolumeSchema,
  cy_mar: ncVolumeSchema,
  cy_apr: ncVolumeSchema,
  cy_may: ncVolumeSchema,
  cy_jun: ncVolumeSchema,
  cy_jul: ncVolumeSchema,
  cy_aug: ncVolumeSchema,
  cy_sep: ncVolumeSchema,
  cy_oct: ncVolumeSchema,
  cy_nov: ncVolumeSchema,
  cy_dec: ncVolumeSchema,
  ny_jan: ncVolumeSchema,
  ny_feb: ncVolumeSchema,
  ny_mar: ncVolumeSchema,
  ny_apr: ncVolumeSchema,
  ny_may: ncVolumeSchema,
  ny_jun: ncVolumeSchema,
  ny_jul: ncVolumeSchema,
  ny_aug: ncVolumeSchema,
  ny_sep: ncVolumeSchema,
  ny_oct: ncVolumeSchema,
  ny_nov: ncVolumeSchema,
  ny_dec: ncVolumeSchema,
})

export const importChannelContributionsDetailsHeaders = getSchemaKeys(
  importChannelContributionsDetailsSchema,
)

// Credit Index Scores
// Column headers are human-readable (and shared with the export) so the file
// round-trips: users export, edit values, and re-import the same shape.
export const CREDIT_INDEX_SCORE_MONTH_HEADERS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
] as const

export const importCreditIndexScoresHeaders = [
  'ERP Number',
  'Business Name',
  'Legal Name',
  'Equifax #',
  'Year',
  ...CREDIT_INDEX_SCORE_MONTH_HEADERS,
] as const

// Month cells are kept as raw strings here; the worker parses/validates them
// (empty ⇒ no change, NULL ⇒ clear the month, otherwise an integer 0–100).
const creditIndexScoreCellSchema = z.string().trim().optional().default('')

/** Stable key for a single score cell, shared by the importer (worker) and the read path (backend). */
export function cellKey(cell: { sapErp: string; year: number; month: number }): string {
  return `${cell.sapErp}|${cell.year}|${cell.month}`
}

export const importCreditIndexScoresSchema = z.object({
  'ERP Number': z.string().trim().optional().default('').pipe(trimLeadingZerosSchema),
  'Business Name': z.string().trim().optional().default(''),
  'Legal Name': z.string().trim().optional().default(''),
  'Equifax #': z.string().trim().optional().default(''),
  Year: z.string().trim().optional().default(''),
  Jan: creditIndexScoreCellSchema,
  Feb: creditIndexScoreCellSchema,
  Mar: creditIndexScoreCellSchema,
  Apr: creditIndexScoreCellSchema,
  May: creditIndexScoreCellSchema,
  Jun: creditIndexScoreCellSchema,
  Jul: creditIndexScoreCellSchema,
  Aug: creditIndexScoreCellSchema,
  Sep: creditIndexScoreCellSchema,
  Oct: creditIndexScoreCellSchema,
  Nov: creditIndexScoreCellSchema,
  Dec: creditIndexScoreCellSchema,
})

// BPC Actuals Details
const bpcKpiColumns = Object.fromEntries(
  BPC_ACTUALS_KPI_COLUMNS.map((column) => [column, ncVolumeSchema]),
)

export const importBpcActualsDetailsSchema = z.object({
  Material: trimLeadingZerosSchema,
  'Sub-channel': z.string(),
  ...bpcKpiColumns,
})

export const importBpcActualsDetailsHeaders = getSchemaKeys(importBpcActualsDetailsSchema)
// Google Financial Statements
// One row per ERP per year; each metric (type) is its own column, immediately followed by that
// metric's comment column. Column headers are human-readable (and shared with the export) so the
// file round-trips: users export, edit values, and re-import the same shape. Value cells are kept
// as raw strings here; the worker parses/validates them (empty ⇒ no change, NULL ⇒ clear the
// metric, otherwise a finite decimal). Comment cells are free text — an empty one clears the note.
const googleFinancialStatementValueCellSchema = z.string().trim().optional().default('')
const googleFinancialStatementCommentCellSchema = z.string().trim().optional().default('')

export const importGoogleFinancialStatementsHeaders = [
  'ERP Number',
  'Business Name',
  'Legal Name',
  'Credit Customer Group',
  'Public / Private',
  'Stock Symbol',
  'Financial Exercise Date',
  'Year',
  GOOGLE_FINANCIAL_STATEMENT_TYPE_HEADERS.netProfit,
  GOOGLE_FINANCIAL_STATEMENT_TYPE_COMMENT_HEADERS.netProfit,
  GOOGLE_FINANCIAL_STATEMENT_TYPE_HEADERS.grossMargin,
  GOOGLE_FINANCIAL_STATEMENT_TYPE_COMMENT_HEADERS.grossMargin,
  GOOGLE_FINANCIAL_STATEMENT_TYPE_HEADERS.currentRatio,
  GOOGLE_FINANCIAL_STATEMENT_TYPE_COMMENT_HEADERS.currentRatio,
  GOOGLE_FINANCIAL_STATEMENT_TYPE_HEADERS.quickRatio,
  GOOGLE_FINANCIAL_STATEMENT_TYPE_COMMENT_HEADERS.quickRatio,
  GOOGLE_FINANCIAL_STATEMENT_TYPE_HEADERS.debtToAssetRatio,
  GOOGLE_FINANCIAL_STATEMENT_TYPE_COMMENT_HEADERS.debtToAssetRatio,
  GOOGLE_FINANCIAL_STATEMENT_TYPE_HEADERS.averageCollectionPeriod,
  GOOGLE_FINANCIAL_STATEMENT_TYPE_COMMENT_HEADERS.averageCollectionPeriod,
  GOOGLE_FINANCIAL_STATEMENT_TYPE_HEADERS.averageDaysPayable,
  GOOGLE_FINANCIAL_STATEMENT_TYPE_COMMENT_HEADERS.averageDaysPayable,
] as const

/** Stable key for a single statement cell, shared by the importer (worker) and the read path (backend). */
export function gfsCellKey(cell: { sapErp: string; type: string; year: number }): string {
  return `${cell.sapErp}|${cell.type}|${cell.year}`
}

// Keys are the literal header labels (kept in sync with GOOGLE_FINANCIAL_STATEMENT_TYPE_HEADERS).
// Using literals rather than computed keys keeps Zod's inferred type precise (no string index
// signature), so invalid-row objects round-trip cleanly through the worker.
export const importGoogleFinancialStatementsSchema = z.object({
  'ERP Number': z.string().trim().optional().default('').pipe(trimLeadingZerosSchema),
  'Business Name': z.string().trim().optional().default(''),
  'Legal Name': z.string().trim().optional().default(''),
  'Credit Customer Group': z.string().trim().optional().default(''),
  'Public / Private': z.string().trim().optional().default(''),
  'Stock Symbol': z.string().trim().optional().default(''),
  'Financial Exercise Date': z.string().trim().optional().default(''),
  Year: z.string().trim().optional().default(''),
  'Net Profit %': googleFinancialStatementValueCellSchema,
  'Net Profit % Comment': googleFinancialStatementCommentCellSchema,
  'GM %': googleFinancialStatementValueCellSchema,
  'GM % Comment': googleFinancialStatementCommentCellSchema,
  'Current Ratio %': googleFinancialStatementValueCellSchema,
  'Current Ratio % Comment': googleFinancialStatementCommentCellSchema,
  'Quick Ratio %': googleFinancialStatementValueCellSchema,
  'Quick Ratio % Comment': googleFinancialStatementCommentCellSchema,
  'Debt-to-Asset Ratio %': googleFinancialStatementValueCellSchema,
  'Debt-to-Asset Ratio % Comment': googleFinancialStatementCommentCellSchema,
  'Average Collection Period (Days)': googleFinancialStatementValueCellSchema,
  'Average Collection Period (Days) Comment': googleFinancialStatementCommentCellSchema,
  'Average Days Payable (Days)': googleFinancialStatementValueCellSchema,
  'Average Days Payable (Days) Comment': googleFinancialStatementCommentCellSchema,
})

// Margins (Retailer/Wholesaler × FMC/NC)
// A marking cell is optional: an empty value clears the marking (⇒ null), any
// other value must be numeric. Commas are stripped so thousands-formatted
// numbers round-trip from the export.
const marginCsvValueSchema = z
  .union([z.string(), z.number()])
  .nullable()
  .default(null)
  .transform((value, ctx) => {
    if (value === null) return null
    const trimmed = String(value).trim()
    if (trimmed === '') return null
    const normalized = trimmed.replace(/,/g, '')
    if (!/^-?\d*\.?\d+$/.test(normalized)) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Invalid numeric value' })
      return z.NEVER
    }
    return normalized
  })

const marginMarkingColumns = Object.fromEntries(
  MARKING_CODE_VALUES.map((marking) => [marking, marginCsvValueSchema]),
)

export const importMarginsFmcSchema = z.object({
  brand: z.string(),
  packContentSize: z.coerce.number(),
  ...marginMarkingColumns,
})
export const importMarginsFmcHeaders = getSchemaKeys(importMarginsFmcSchema)

export const importMarginsNcSchema = z.object({
  productType: z.string(),
  subChannel: z.string(),
  ...marginMarkingColumns,
})
export const importMarginsNcHeaders = getSchemaKeys(importMarginsNcSchema)

// Federal excise mappings: one row per product category × key × marking (long format). `name` is the
// export's informational last column (the material group's or product type's name). The parser reads
// columns by position and rejects a row whose column count differs, so the column is declared here and
// ignored on import for an exported file to round-trip.
export const importFederalExciseMappingsSchema = z.object({
  product_category: z.string(),
  key: z.string(),
  marking_code: z.string(),
  price_fed_excise: marginCsvValueSchema,
  name: z.string().nullable().default(null),
})
export const importFederalExciseMappingsHeaders = getSchemaKeys(importFederalExciseMappingsSchema)

export const importAgreementMappingsSchema = z.object({
  masterAgreement: z.string(),
  contractClause: z
    .string()
    .nullable()
    .default(null)
    .transform((value) => (value && value.trim() !== '' ? value.trim() : null)),
})

export const importAgreementMappingsHeaders = getSchemaKeys(importAgreementMappingsSchema)

// Key Account Contract Estimates
// An estimate value cell is optional: an empty value is treated as `0`, any
// other value must be numeric. Commas are stripped so thousands-formatted
// numbers round-trip from the export.
const estimateCsvValueSchema = z
  .union([z.string(), z.number()])
  .nullable()
  .default(null)
  .transform((value, ctx) => {
    if (value === null) return '0'
    const trimmed = String(value).trim()
    if (trimmed === '') return '0'
    const normalized = trimmed.replace(/,/g, '')
    if (!/^-?\d*\.?\d+$/.test(normalized)) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Invalid numeric value' })
      return z.NEVER
    }
    return normalized
  })

export const importKeyAccountContractEstimatesSchema = z.object({
  hierarchy_erp: z.string(),
  account_name: z.string().nullable().optional(),
  fmc_conditional: estimateCsvValueSchema,
  fmc_performance: estimateCsvValueSchema,
  fmc_sla: estimateCsvValueSchema,
  vapour_conditional: estimateCsvValueSchema,
  vapour_performance: estimateCsvValueSchema,
  vapour_sla: estimateCsvValueSchema,
  nrt_conditional: estimateCsvValueSchema,
  nrt_performance: estimateCsvValueSchema,
  nrt_sla: estimateCsvValueSchema,
})

export const importKeyAccountContractEstimatesHeaders = getSchemaKeys(
  importKeyAccountContractEstimatesSchema,
)
